// src/pages/VendorPage.jsx — Shop World Mobile Style
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  getVendorById, getItemsByVendorId,
  getVendorCategories,
  addReview, getReviews
} from '../firebase/services';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import Toast from '../components/Toast';
import './VendorPage.css';

function useDebounce(value, delay) {
  const [dv, setDv] = useState(value);
  useEffect(() => { const t = setTimeout(() => setDv(value), delay); return () => clearTimeout(t); }, [value, delay]);
  return dv;
}

/* ── Loading Spinner ── */
const LoadingSpinner = () => (
  <div style={{ position:'fixed', inset:0, background:'#fff', display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:16, zIndex:9999 }}>
    <div className="spinner"/>
    <p style={{ color:'#aaa', fontSize:12, fontFamily:'Inter,sans-serif', letterSpacing:'0.12em', textTransform:'uppercase' }}>Loading Menu</p>
  </div>
);

export default function VendorPage() {
  const { vendorId } = useParams();
  const navigate     = useNavigate();
  const { addToCart, cart, totalItems, subtotal, updateQty, removeFromCart } = useCart();
  const { user }     = useAuth();

  const [vendor,       setVendor]      = useState(null);
  const [items,        setItems]       = useState([]);
  const [vendorCats,   setVendorCats]  = useState([]);   // vendor_categories
  const [reviews,      setReviews]     = useState([]);
  const [loading,      setLoading]     = useState(true);
  const [activeSubcat, setActiveSubcat]= useState('all');
  const [search,       setSearch]      = useState('');
  const [toast,        setToast]       = useState(null);
  const [reviewText,   setReviewText]  = useState('');
  const [reviewRating, setReviewRating]= useState(5);
  const [submitting,   setSubmitting]  = useState(false);
  const [tab,          setTab]         = useState('menu');
  const [barBump,      setBarBump]     = useState(false);
  const [expandedCats, setExpandedCats]= useState({});  // which category sections are collapsed
  const catChipsRef = useRef(null);

  const debouncedSearch = useDebounce(search, 280);

  // ── Load data ──
  useEffect(() => {
    let alive = true;
    setLoading(true);
    async function load() {
      try {
        const [v, allItems, revs, vcats] = await Promise.all([
          getVendorById(vendorId),
          getItemsByVendorId(vendorId),
          getReviews(vendorId),
          getVendorCategories(vendorId),
        ]);
        if (!alive) return;
        if (!v) { navigate('/'); return; }
        setVendor(v);
        setItems(allItems || []);
        setReviews(revs || []);
        setVendorCats(vcats || []);
        // All expanded by default
        const expanded = {};
        (vcats || []).forEach(c => { expanded[c.id] = true; });
        setExpandedCats(expanded);
      } catch (err) { console.error(err); if (alive) navigate('/'); }
      finally { if (alive) setLoading(false); }
    }
    load();
    return () => { alive = false; };
  }, [vendorId, navigate]);

  // Cart bar bump
  useEffect(() => {
    if (totalItems > 0) { setBarBump(true); const t = setTimeout(() => setBarBump(false), 380); return () => clearTimeout(t); }
  }, [totalItems]);

  const getCartQty = useCallback((id) => {
    const f = cart.find(c => c._key === `${id}__` || c._key.startsWith(`${id}__`));
    return f ? f.qty : 0;
  }, [cart]);
  const getCartKey = useCallback((id) => {
    const f = cart.find(c => c._key === `${id}__` || c._key.startsWith(`${id}__`));
    return f?._key || null;
  }, [cart]);

  const handleAdd = useCallback((item) => {
    addToCart({ id:item.id, name:item.ItemName, price:item.TotalPrice||item.ItemPrice, image:item.ImageBlob||'', type:'food', vendorName:vendor?.name, vendorId });
    setToast({ message: `${item.ItemName} added!`, type: 'success' });
  }, [addToCart, vendor, vendorId]);

  const handleInc = useCallback((item) => {
    const key = getCartKey(item.id);
    if (key) updateQty(key, getCartQty(item.id) + 1);
  }, [getCartKey, getCartQty, updateQty]);

  const handleDec = useCallback((item) => {
    const key = getCartKey(item.id);
    if (!key) return;
    const cur = getCartQty(item.id);
    cur <= 1 ? removeFromCart(key) : updateQty(key, cur - 1);
  }, [getCartKey, getCartQty, updateQty, removeFromCart]);

  const handleReview = async (e) => {
    e.preventDefault();
    if (!user) { navigate('/login'); return; }
    setSubmitting(true);
    try {
      await addReview(vendorId, user.uid, user.displayName || 'Guest', reviewRating, reviewText);
      setReviews(await getReviews(vendorId));
      setReviewText(''); setReviewRating(5);
      setToast({ message: 'Review submitted!', type: 'success' });
    } finally { setSubmitting(false); }
  };

  // Items filtered by search + subcategory
  const filteredItems = useMemo(() => {
    let list = items;
    if (activeSubcat !== 'all') list = list.filter(i => i.SubCategoryId === activeSubcat);
    if (debouncedSearch.trim()) {
      const q = debouncedSearch.toLowerCase();
      list = list.filter(i => i.ItemName?.toLowerCase().includes(q) || i.ItemDescription?.toLowerCase().includes(q));
    }
    return list;
  }, [items, activeSubcat, debouncedSearch]);

  // Group filtered items by vendor_category
  const groupedItems = useMemo(() => {
    if (vendorCats.length === 0) return [{ cat: null, items: filteredItems }];
    const groups = vendorCats.map(cat => ({
      cat,
      items: filteredItems.filter(i => i.SubCategoryId === cat.id || i.SubCategoryId === cat.name),
    })).filter(g => g.items.length > 0);
    // Uncategorised items
    const categorisedIds = new Set(groups.flatMap(g => g.items.map(i => i.id)));
    const uncategorised = filteredItems.filter(i => !categorisedIds.has(i.id));
    if (uncategorised.length > 0) groups.push({ cat: null, items: uncategorised });
    return groups.length > 0 ? groups : [{ cat: null, items: filteredItems }];
  }, [filteredItems, vendorCats]);

  // Scroll chip into view on category select
  const handleCatSelect = (id) => {
    setActiveSubcat(id);
    // Scroll to section
    if (id !== 'all') {
      setTimeout(() => {
        const el = document.getElementById(`cat-section-${id}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 50);
    }
  };

  if (loading) return <LoadingSpinner />;
  if (!vendor)  return null;

  const isOpen = (() => {
    if (!vendor?.openTime) return true;
    const now = new Date();
    const day = ['sunday','monday','tuesday','wednesday','thursday','friday','saturday'][now.getDay()];
    const h = vendor.openTime[day];
    if (!h) return false;
    const [oh,om] = h.open.split(':').map(Number);
    const [ch,cm] = h.close.split(':').map(Number);
    const mins = now.getHours()*60+now.getMinutes();
    return mins >= oh*60+om && mins <= ch*60+cm;
  })();

  /* ── Item Row (mobile app style) ── */
  const ItemRow = ({ item }) => {
    const qty   = getCartQty(item.id);
    const price = item.TotalPrice || item.ItemPrice || 0;
    return (
      <div className="vp-item-row">
        <div className="vp-item-row-img">
          {item.ImageBlob
            ? <img src={item.ImageBlob} alt={item.ItemName} loading="lazy"/>
            : <div className="vp-item-row-img-ph">🍽</div>}
        </div>
        <div className="vp-item-row-info">
          {item.isVeg !== undefined && (
            <span className={`vp-veg-sq ${item.isVeg?'veg':'nonveg'}`}/>
          )}
          <div className="vp-item-row-name">{item.ItemName}</div>
          {item.ItemDescription && (
            <div className="vp-item-row-desc">{item.ItemDescription}</div>
          )}
          <div className="vp-item-row-price">₹{price}</div>
          <div className="vp-item-row-ctrl">
            {qty > 0 ? (
              <div className="vp-mob-stepper">
                <button onClick={() => handleDec(item)}>−</button>
                <span>{qty}</span>
                <button onClick={() => handleInc(item)}>+</button>
              </div>
            ) : (
              <button className="vp-mob-add-btn" onClick={() => handleAdd(item)}>Add</button>
            )}
          </div>
        </div>
      </div>
    );
  };

  /* ── Desktop Item Card ── */
  const ItemCard = ({ item }) => {
    const qty = getCartQty(item.id);
    const price = item.TotalPrice || item.ItemPrice || 0;
    return (
      <div className="vp-item-card">
        <div className="vp-item-img-wrap">
          {item.ImageBlob
            ? <img src={item.ImageBlob} alt={item.ItemName} className="vp-item-img" loading="lazy"/>
            : <div className="vp-item-img vp-item-img-placeholder">🍽</div>}
          {item.isVeg !== undefined && (
            <span className={`vp-veg-badge ${item.isVeg?'veg':'nonveg'}`}>{item.isVeg?'🟢 Veg':'🔴 Non-Veg'}</span>
          )}
        </div>
        <div className="vp-item-body">
          <h3 className="vp-item-name">{item.ItemName}</h3>
          {item.ItemDescription && <p className="vp-item-desc">{item.ItemDescription}</p>}
          <div className="vp-item-footer">
            <span className="vp-item-price">₹{price}</span>
            {qty > 0 ? (
              <div className="vp-stepper">
                <button className="vp-stepper-btn" onClick={() => handleDec(item)}>−</button>
                <span className="vp-stepper-qty">{qty}</span>
                <button className="vp-stepper-btn vp-stepper-inc" onClick={() => handleInc(item)}>+</button>
              </div>
            ) : (
              <button className="vp-add-btn" onClick={() => handleAdd(item)}><span className="vp-add-plus">+</span> Add</button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="vendor-page-container">
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}

      {/* ══════════════════════════════════════
          MOBILE VIEW  (≤ 768px)
      ══════════════════════════════════════ */}
      <div className="vp-mobile-view">

        {/* Mobile Header */}
        <div className="vp-mob-header">
          <button className="vp-mob-back" onClick={() => navigate(-1)}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="m15 18-6-6 6-6"/></svg>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="m15 18-6-6 6-6"/></svg>
          </button>
          <span className="vp-mob-header-title">{vendor.name}</span>
          <button className="vp-mob-cart-btn" onClick={() => navigate('/cart')}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>
            {totalItems > 0 && <span className="vp-mob-cart-badge">{totalItems}</span>}
          </button>
        </div>

        {/* Search */}
        <div className="vp-mob-search-wrap">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#E63946" strokeWidth="2.5"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
          <input
            className="vp-mob-search"
            placeholder="Search items..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        {/* Category chips */}
        <div className="vp-mob-cats" ref={catChipsRef}>
          <button
            className={`vp-mob-cat-chip${activeSubcat==='all'?' active':''}`}
            onClick={() => handleCatSelect('all')}
          >
            <span className="vp-mob-cat-dot active-dot"/>All
          </button>
          {vendorCats.map(cat => (
            <button
              key={cat.id}
              className={`vp-mob-cat-chip${activeSubcat===cat.id?' active':''}`}
              onClick={() => handleCatSelect(cat.id)}
            >
              {activeSubcat === cat.id && <span className="vp-mob-cat-dot"/>}
              {cat.name}
            </button>
          ))}
        </div>

        {/* Menu grouped by category */}
        <div className="vp-mob-menu">
          {filteredItems.length === 0 ? (
            <div className="vp-mob-empty">
              <div style={{fontSize:44,marginBottom:12}}>🍽</div>
              <p>No items found{search ? ` for "${search}"` : ''}.</p>
            </div>
          ) : (
            groupedItems.map(({ cat, items: catItems }, gi) => (
              <div key={cat?.id || 'other'} id={cat ? `cat-section-${cat.id}` : 'cat-section-other'} className="vp-mob-cat-section">
                {/* Category heading */}
                <button
                  className="vp-mob-cat-heading"
                  onClick={() => setExpandedCats(p => ({ ...p, [cat?.id||'other']: !p[cat?.id||'other'] }))}
                >
                  <span className="vp-mob-cat-heading-name">{cat?.name || 'Menu Items'}</span>
                  <svg className={`vp-mob-cat-chevron${expandedCats[cat?.id||'other']!==false?'':' collapsed'}`} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="m6 9 6 6 6-6"/></svg>
                </button>

                {/* Items */}
                {expandedCats[cat?.id||'other'] !== false && (
                  <div className="vp-mob-cat-items">
                    {catItems.map(item => <ItemRow key={item.id} item={item}/>)}
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* Cart Summary Bar */}
        {totalItems > 0 && (
          <div className={`vp-mob-cart-bar${barBump?' bump':''}`}>
            <div className="vp-mob-cart-bar-inner">
              <div className="vp-mob-cart-info">
                <div className="vp-mob-cart-counts">Total Items: <strong>{totalItems}</strong></div>
                <div className="vp-mob-cart-total">Total Price: <strong>₹{subtotal.toFixed(2)}</strong></div>
              </div>
              <button className="vp-mob-cart-remove" onClick={() => removeFromCart(cart[0]?._key)}>Remove</button>
            </div>
            <button className="vp-mob-checkout-btn" onClick={() => navigate('/checkout')}>
              CHECKOUT
            </button>
          </div>
        )}
      </div>

      {/* ══════════════════════════════════════
          DESKTOP VIEW  (> 768px)
      ══════════════════════════════════════ */}
      <div className="vp-desktop-view">

        {/* Hero */}
        <div className="vendor-hero">
          <div className="vendor-hero-bg" style={{ backgroundImage: vendor.imageBlob ? `url('${vendor.imageBlob}')` : 'none' }}/>
          <div className="vendor-hero-overlay"/>
          <div className="vendor-hero-content">
            <div className="vendor-breadcrumb">
              <Link to="/">Home</Link><span>/</span>
              <Link to="/shop">Vendors</Link><span>/</span>
              <span>{vendor.name}</span>
            </div>
            <div className="vendor-header-row">
              <div>
                <div className="vendor-tags">
                  <span className={`vendor-tag ${isOpen?'tag-open':'tag-closed'}`}>{isOpen?'● Open Now':'● Closed'}</span>
                  {vendor.recommended && <span className="vendor-tag tag-recommended">★ Recommended</span>}
                </div>
                <h1 className="vendor-title">{vendor.name}</h1>
                <div className="vendor-meta">
                  {vendor.area?.city && <span>📍 {vendor.area.city}</span>}
                  {vendor.phone     && <span>📞 {vendor.phone}</span>}
                  {vendor.deliveryCharge===0 && <span style={{color:'#5ddc9e',fontWeight:600}}>🛵 Free Delivery</span>}
                </div>
              </div>
            </div>
          </div>
        </div>

        {vendor.offerEnabled && vendor.offer && (
          <div className="offer-banner"><span>🎁</span><span className="offer-text">{vendor.offer}</span></div>
        )}

        {/* Desktop tabs */}
        <div className="tabs-header">
          {[['menu','Menu'],['reviews',`Reviews (${reviews.length})`],['info','Info']].map(([k,l]) => (
            <button key={k} onClick={() => setTab(k)} className={`tab-btn ${tab===k?'active':''}`}>{l}</button>
          ))}
        </div>

        {/* MENU */}
        {tab === 'menu' && (
          <div className="vendor-layout">
            <aside className="vendor-sidebar">
              <div className="sidebar-box">
                <div className="sidebar-title">Menu</div>
                {vendor.restaurantType && <div className="sidebar-main-cat"><span>📂</span> {vendor.restaurantType}</div>}
                <input className="search-input" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search items…"/>
                <div className="category-list">
                  <button onClick={() => setActiveSubcat('all')} className={`cat-btn ${activeSubcat==='all'?'active':''}`}>
                    <span className="cat-icon">🍽</span><span>All Items</span>
                  </button>
                  {vendorCats.map(cat => (
                    <button key={cat.id} onClick={() => setActiveSubcat(cat.id)} className={`cat-btn ${activeSubcat===cat.id?'active':''}`}>
                      {cat.vendorImage
                        ? <img src={cat.vendorImage} alt={cat.name} className="cat-icon"/>
                        : <span className="cat-icon">🍽</span>}
                      <span>{cat.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            </aside>

            <div className="vp-items-area">
              {filteredItems.length === 0 ? (
                <div style={{textAlign:'center',padding:'80px 0',color:'#aaa'}}>
                  <div style={{fontSize:44,marginBottom:14}}>🍽</div>
                  <p style={{fontSize:13}}>No items found{search?` for "${search}"`:''}</p>
                </div>
              ) : (
                groupedItems.map(({ cat, items: catItems }) => (
                  <div key={cat?.id||'other'} className="vp-cat-group">
                    {cat && <div className="vp-cat-group-title">{cat.name}</div>}
                    <div className="vp-items-grid">
                      {catItems.map(item => <ItemCard key={item.id} item={item}/>)}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* REVIEWS */}
        {tab === 'reviews' && (
          <div style={{maxWidth:760,margin:'0 auto',padding:'36px 20px 140px'}}>
            {user ? (
              <form onSubmit={handleReview} style={{background:'#f4f6fa',border:'1px solid #ececec',borderRadius:12,padding:24,marginBottom:24}}>
                <h4 style={{fontFamily:'Inter,sans-serif',fontSize:18,marginBottom:18,color:'#253d4e',fontWeight:700}}>Write a Review</h4>
                <div style={{marginBottom:14}}>
                  <div style={{fontSize:10,fontWeight:600,color:'#3bb77e',letterSpacing:'0.14em',textTransform:'uppercase',marginBottom:8}}>Rating</div>
                  <div style={{display:'flex',gap:3}}>{[1,2,3,4,5].map(n=><button key={n} type="button" onClick={()=>setReviewRating(n)} style={{background:'none',border:'none',fontSize:26,color:n<=reviewRating?'#f1a50e':'#ddd',cursor:'pointer',padding:'0 2px'}}>★</button>)}</div>
                </div>
                <textarea rows={3} value={reviewText} onChange={e=>setReviewText(e.target.value)} required placeholder="Share your experience…" style={{width:'100%',padding:'12px 14px',borderRadius:8,border:'1.5px solid #e8e8e8',background:'#fff',color:'#253d4e',fontSize:13,fontFamily:'Inter,sans-serif',resize:'vertical',outline:'none',boxSizing:'border-box'}}/>
                <button type="submit" disabled={submitting} style={{marginTop:14,padding:'10px 26px',background:'#3bb77e',color:'#fff',border:'none',borderRadius:8,fontWeight:600,fontSize:12,cursor:'pointer',fontFamily:'Inter,sans-serif'}}>{submitting?'Submitting…':'Submit Review'}</button>
              </form>
            ) : (
              <div style={{background:'#f4f6fa',border:'1px solid #ececec',borderRadius:12,padding:24,marginBottom:24,textAlign:'center'}}>
                <p style={{color:'#888',marginBottom:14,fontSize:13}}>Sign in to leave a review</p>
                <Link to="/login" style={{padding:'10px 24px',background:'#3bb77e',color:'#fff',borderRadius:8,textDecoration:'none',fontWeight:600,fontSize:12,fontFamily:'Inter,sans-serif'}}>Sign In</Link>
              </div>
            )}
            {reviews.length===0?<p style={{color:'#aaa',textAlign:'center',fontSize:13}}>No reviews yet.</p>:reviews.map(r=>(
              <div key={r.id} style={{background:'#fff',border:'1px solid #ececec',borderRadius:10,padding:18,marginBottom:10}}>
                <div style={{display:'flex',justifyContent:'space-between',marginBottom:8}}>
                  <div><span style={{fontSize:14,fontWeight:600,color:'#253d4e',fontFamily:'Inter,sans-serif'}}>{r.displayName}</span><div style={{color:'#f1a50e',fontSize:12,marginTop:2}}>{'★'.repeat(r.rating)}{'☆'.repeat(5-r.rating)}</div></div>
                  <span style={{fontSize:11,color:'#bbb'}}>{r.createdAt?.toDate?.()?.toLocaleDateString()}</span>
                </div>
                <p style={{fontSize:13,color:'#555',lineHeight:1.7,margin:0,fontFamily:'Inter,sans-serif'}}>{r.comment}</p>
              </div>
            ))}
          </div>
        )}

        {/* INFO */}
        {tab === 'info' && (
          <div style={{maxWidth:760,margin:'0 auto',padding:'36px 20px 140px'}}>
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))',gap:12}}>
              {[['Restaurant',vendor.name],['Phone',vendor.phone||'—'],['Type',vendor.restaurantType||'—'],['Delivery',vendor.deliveryCharge===0?'Free':vendor.deliveryCharge>0?`₹${vendor.deliveryCharge}`:'—']].map(([k,v])=>(
                <div key={k} style={{background:'#f4f6fa',border:'1px solid #ececec',borderRadius:10,padding:18}}>
                  <div style={{fontSize:9,textTransform:'uppercase',color:'#3bb77e',marginBottom:6,letterSpacing:'0.16em',fontWeight:600,fontFamily:'Inter,sans-serif'}}>{k}</div>
                  <div style={{fontSize:14,color:'#253d4e',fontWeight:600,fontFamily:'Inter,sans-serif'}}>{v}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Desktop floating cart bar */}
        {totalItems > 0 && (
          <div className={`vp-cart-bar ${barBump?'vp-cart-bar-bump':''}`}>
            <div className="vp-cart-bar-inner">
              <div className="vp-cart-bar-left">
                <div className="vp-cart-count-badge">{totalItems}</div>
                <div className="vp-cart-bar-label">
                  <span className="vp-cart-bar-title">{totalItems} item{totalItems!==1?'s':''} in cart</span>
                  <span className="vp-cart-bar-vendor">{vendor.name}</span>
                </div>
              </div>
              <div className="vp-cart-bar-right">
                <div className="vp-cart-bar-total">₹{subtotal.toFixed(2)}</div>
                <button className="vp-cart-bar-btn" onClick={() => navigate('/checkout')}>Checkout →</button>
              </div>
            </div>
          </div>
        )}
      </div>

    </div>
  );
}
